import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { passengerAdmin, getAdminConfigWarning, readBases } from "../components/services/adminBackendFetch";
import { AdminPageHeader } from "../components/AdminPageHeader";

const STATUS_META = {
    free: { label: "Online & free", color: "#22c55e", severity: "success" },
    accepted: { label: "Accepted ride", color: "#eab308", severity: "warning" },
    ongoing: { label: "Ongoing ride", color: "#ef4444", severity: "danger" },
};

const POLL_MS = 8000;

function loadGoogleMaps(apiKey) {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (!apiKey) return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));

    const existing = document.getElementById("swyft-google-maps-script");
    if (existing) {
        if (window.google?.maps) return Promise.resolve(window.google.maps);
        return new Promise((resolve, reject) => {
            existing.addEventListener("load", () => {
                if (window.google?.maps) resolve(window.google.maps);
                else reject(new Error("Google Maps failed to initialize"));
            });
            existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")));
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = "swyft-google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google?.maps) resolve(window.google.maps);
            else reject(new Error("Google Maps script loaded but maps API is unavailable"));
        };
        script.onerror = () => reject(new Error("Google Maps failed to load — check API key / Maps JavaScript API"));
        document.head.appendChild(script);
    });
}

function carSvgDataUrl(hex) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="18" fill="${hex}" opacity="0.95"/>
      <path fill="#fff" d="M11 24h2.2l1.1-3.2h11.4L27 24H29v-1.6c0-.5-.2-1-.6-1.3l-1.6-1.2-.9-2.5c-.3-.8-1.1-1.4-2-1.4H15.1c-.9 0-1.7.6-2 1.4l-.9 2.5-1.6 1.2c-.4.3-.6.8-.6 1.3V24zm5.2-7.2h7.6l.7 2H15.7l.5-2zM14.2 26.2a1.6 1.6 0 100-3.2 1.6 1.6 0 000 3.2zm11.6 0a1.6 1.6 0 100-3.2 1.6 1.6 0 000 3.2z"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export default function LiveMapPage() {
    const { googleMapsApiKey } = readBases();
    const configWarning = getAdminConfigWarning();
    const apiKey = (googleMapsApiKey || "").trim();
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const mapsApiRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [mapReady, setMapReady] = useState(false);
    const [error, setError] = useState(null);
    const [mapError, setMapError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const clearMarkers = () => {
        for (const m of markersRef.current) m.setMap(null);
        markersRef.current = [];
    };

    const renderMarkers = useCallback((maps, data) => {
        if (!mapInstance.current || !maps) return;
        clearMarkers();
        const drivers = data?.drivers || [];
        const bounds = new maps.LatLngBounds();
        for (const d of drivers) {
            const meta = STATUS_META[d.map_status] || STATUS_META.free;
            const marker = new maps.Marker({
                position: { lat: d.latitude, lng: d.longitude },
                map: mapInstance.current,
                title: `${d.full_name || `Driver #${d.id}`} — ${meta.label}`,
                icon: {
                    url: carSvgDataUrl(meta.color),
                    scaledSize: new maps.Size(36, 36),
                    anchor: new maps.Point(18, 18),
                },
            });
            const info = new maps.InfoWindow({
                content: `<div style="font:13px/1.4 system-ui,sans-serif;min-width:160px">
                  <strong>${d.full_name || `Driver #${d.id}`}</strong><br/>
                  ${meta.label}<br/>
                  ${d.vehicle_number ? `Car: ${d.vehicle_number}<br/>` : ""}
                  ${d.phone ? `Phone: ${d.phone}` : ""}
                </div>`,
            });
            marker.addListener("click", () => info.open({ map: mapInstance.current, anchor: marker }));
            markersRef.current.push(marker);
            bounds.extend({ lat: d.latitude, lng: d.longitude });
        }
        if (drivers.length > 0) {
            mapInstance.current.fitBounds(bounds, 64);
        } else if (data?.default_location) {
            mapInstance.current.setCenter({
                lat: Number(data.default_location.latitude),
                lng: Number(data.default_location.longitude),
            });
            mapInstance.current.setZoom(11);
        }
        // Force paint after container layout (fixes blank gray map).
        maps.event.trigger(mapInstance.current, "resize");
    }, []);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await passengerAdmin("/admin/map/drivers");
            setPayload(data);
            if (mapInstance.current && mapsApiRef.current) {
                renderMarkers(mapsApiRef.current, data);
            }
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [renderMarkers]);

    // Initialize Google Map once (do not re-create on every poll).
    useEffect(() => {
        if (configWarning) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;

        (async () => {
            try {
                await load();
                if (cancelled) return;

                if (!apiKey) {
                    setMapError("Set REACT_APP_GOOGLE_MAPS_API_KEY in .env and restart the admin app.");
                    return;
                }

                const maps = await loadGoogleMaps(apiKey);
                if (cancelled || !mapRef.current) return;
                mapsApiRef.current = maps;

                const center = { lat: 43.6532, lng: -79.3832 };
                mapInstance.current = new maps.Map(mapRef.current, {
                    center,
                    zoom: 11,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                });
                setMapReady(true);

                // Resize after paint so tiles render correctly.
                requestAnimationFrame(() => {
                    if (cancelled || !mapInstance.current) return;
                    maps.event.trigger(mapInstance.current, "resize");
                    if (payload) renderMarkers(maps, payload);
                    else load();
                });
            } catch (e) {
                if (!cancelled) {
                    setMapError(e?.message || String(e));
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            clearMarkers();
            mapInstance.current = null;
            mapsApiRef.current = null;
            setMapReady(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally init once per key/config
    }, [apiKey, configWarning]);

    // Re-render markers when payload updates after map is ready.
    useEffect(() => {
        if (!mapReady || !payload || !mapsApiRef.current) return;
        renderMarkers(mapsApiRef.current, payload);
    }, [mapReady, payload, renderMarkers]);

    useEffect(() => {
        if (!autoRefresh || configWarning) return undefined;
        const id = setInterval(() => {
            load();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [autoRefresh, configWarning, load]);

    if (configWarning) {
        return <Message severity="warn" text={configWarning} className="w-full" />;
    }

    const counts = payload?.counts || { free: 0, accepted: 0, ongoing: 0, total: 0 };

    return (
        <div>
            <AdminPageHeader
                title="Live map"
                subtitle="Online SWYFT drivers only. Green = free, yellow = accepted, red = ongoing. Locations refresh automatically."
            />

            <div className="flex flex-wrap gap-2 mb-3 align-items-center">
                <Tag severity="success" value={`Free ${counts.free}`} icon="pi pi-car" />
                <Tag severity="warning" value={`Accepted ${counts.accepted}`} icon="pi pi-car" />
                <Tag severity="danger" value={`Ongoing ${counts.ongoing}`} icon="pi pi-car" />
                <Tag value={`Total ${counts.total}`} className="ml-1" />
                <div className="flex-1" />
                <Button
                    label={autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
                    icon={autoRefresh ? "pi pi-sync" : "pi pi-pause"}
                    outlined
                    className="p-button-sm"
                    onClick={() => setAutoRefresh((v) => !v)}
                />
                <Button label="Refresh" icon="pi pi-refresh" className="p-button-sm" onClick={load} />
            </div>

            {error ? <Message severity="error" text={error} className="w-full mb-3" /> : null}
            {mapError ? <Message severity="warn" text={mapError} className="w-full mb-3" /> : null}

            <div className="uc-admin-map-shell surface-card border-round-xl overflow-hidden relative">
                {loading ? (
                    <div className="uc-admin-map-loading flex align-items-center justify-content-center">
                        <ProgressSpinner />
                    </div>
                ) : null}
                <div ref={mapRef} className="uc-admin-map-canvas" />
                {!apiKey ? (
                    <div className="p-3 text-600 text-sm">
                        Map tiles need a Google Maps API key. Driver list still loads from the API when available.
                    </div>
                ) : null}
            </div>

            {(payload?.drivers || []).length > 0 ? (
                <div className="mt-3 surface-card border-round-xl p-3">
                    <div className="font-semibold mb-2">Online drivers</div>
                    <div className="grid">
                        {payload.drivers.map((d) => {
                            const meta = STATUS_META[d.map_status] || STATUS_META.free;
                            return (
                                <div key={d.id} className="col-12 md:col-4 lg:col-3">
                                    <div className="border-1 surface-border border-round p-2 text-sm">
                                        <div className="flex justify-content-between align-items-center gap-2">
                                            <span className="font-medium truncate">
                                                {d.full_name || `Driver #${d.id}`}
                                            </span>
                                            <Tag severity={meta.severity} value={meta.label} />
                                        </div>
                                        <div className="text-600 mt-1">
                                            {d.vehicle_number || "No plate"} · {d.latitude.toFixed(4)},{" "}
                                            {d.longitude.toFixed(4)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : !loading && !error ? (
                <Message
                    severity="info"
                    className="w-full mt-3"
                    text="No online drivers with GPS yet. Drivers must be online and sharing location (PATCH /users/location)."
                />
            ) : null}
        </div>
    );
}
